import { z } from 'zod';
import { BinScatterCrossSelectCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/BinScatterCrossSelectDefinition';
import { BinScatterMultiSelectCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectDefinition';
import { PipelineAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PipelineAnalysisDefinition';
import { BiasAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/BiasAnalysisDefinition';
import { HiringCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HiringCopilotDefinition';
import { PromotionCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PromotionCopilotDefinition';
import { PayBenchmarkingCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PayBenchmarkingDefinition';
import { PayCorrectionCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/PayCorrectionDefinition';
import { SuccessPhenotypeV2CustomVizDefinition } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeDefinition';
import { TopLevelAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TopLevelAnalysisDefinition';
import { ProgressCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/ProgressDefinition';
import { HeatmapAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisDefinition';
import { CustomVizDefinition } from 'libs/end-to-end/src/custom-viz-definition';
import { NlpGraphCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/NlpGraphDefinition';
import { TableViewCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TableViewDefinition';
import { TimeSeriesTrendsCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsDefinition';
import { SummaryStatsCustomVizDefinition } from 'libs/end-to-end/src/summary-stats-definition';
import { RichTextCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/RichTextDefinition';
import { CustomVizId } from 'libs/end-to-end/src/custom-viz/CustomVizId';
import { GeolocationCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/GeolocationDefinition';
import { ExternalStreamlitCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/ExternalStreamlitDefinition';

export const CustomVizStartResponse = z.object({
  staleOutputContents: z.string().nullable(),
  staleCreatedAt: z.number().nullable(),
  started: z.boolean(),
});

export type CustomVizStartResponse = z.infer<typeof CustomVizStartResponse>;

export const CustomVizPollCompleteResponse = z.object({
  pollStatus: z.literal('complete'),
  outputContents: z.string(),
  createdAt: z.number(),
});

export type CustomVizPollCompleteResponse = z.infer<
  typeof CustomVizPollCompleteResponse
>;

export const CustomVizPollPendingResponse = z.object({
  pollStatus: z.literal('pending'),
});

export type CustomVizPollPendingResponse = z.infer<
  typeof CustomVizPollPendingResponse
>;

export const CustomVizPollFailResponse = z.object({
  pollStatus: z.literal('fail'),
  createdAt: z.number(),
});

export type CustomVizPollFailResponse = z.infer<
  typeof CustomVizPollFailResponse
>;

export const CustomVizPollResponse = z.union([
  CustomVizPollCompleteResponse,
  CustomVizPollPendingResponse,
  CustomVizPollFailResponse,
]);

export type CustomVizPollResponse = z.infer<typeof CustomVizPollResponse>;

export const CustomVizOptionsSummaryDropdown = z.object({
  dropdownId: z.string(),
  dropdownItemValue: z.string(),
  dropdownItemText: z.string(),
});

export type CustomVizOptionsSummaryDropdown = z.infer<
  typeof CustomVizOptionsSummaryDropdown
>;

export const CustomVizOptionsSummary = z.object({
  dropdowns: z.array(CustomVizOptionsSummaryDropdown),
});

export type CustomVizOptionsSummary = z.infer<typeof CustomVizOptionsSummary>;

export const isCustomVizDefinitionCopilot = (
  customVizDefinition: CustomVizDefinition,
): customVizDefinition is CustomVizDefinition & {
  customVizId: 'hiringCopilot' | 'promotionCopilot';
} => {
  return (
    customVizDefinition.customVizId === 'hiringCopilot' ||
    customVizDefinition.customVizId === 'promotionCopilot'
  );
};

export const lookupCustomVizDefinition = (
  customVizId: CustomVizId,
): CustomVizDefinition => {
  return {
    biasAnalysis: BiasAnalysisCustomVizDefinition,
    progress: ProgressCustomVizDefinition,
    pipelineAnalysis: PipelineAnalysisCustomVizDefinition,
    heatmap: HeatmapAnalysisCustomVizDefinition,
    binScatterMultiSelect: BinScatterMultiSelectCustomVizDefinition,
    binScatterCrossSelect: BinScatterCrossSelectCustomVizDefinition,
    hiringCopilot: HiringCopilotCustomVizDefinition,
    promotionCopilot: PromotionCopilotCustomVizDefinition,
    payBenchmarking: PayBenchmarkingCustomVizDefinition,
    payCorrection: PayCorrectionCustomVizDefinition,
    successPhenotypeV2: SuccessPhenotypeV2CustomVizDefinition,
    topLevelAnalysis: TopLevelAnalysisCustomVizDefinition,
    nlpGraph: NlpGraphCustomVizDefinition,
    tableView: TableViewCustomVizDefinition,
    timeSeriesTrends: TimeSeriesTrendsCustomVizDefinition,
    summaryStats: SummaryStatsCustomVizDefinition,
    richText: RichTextCustomVizDefinition,
    geolocation: GeolocationCustomVizDefinition,
    externalStreamlit: ExternalStreamlitCustomVizDefinition,
  }[customVizId];
};
